/**
 * CSS loading & patching for Shadow DOM.
 *
 * Fetches external stylesheets, rewrites `:root` ? `:host` so that
 * CSS custom properties work correctly inside the shadow boundary,
 * then injects the result as a <style> element.
 */

/**
 * Base styles that are always injected (layout + modal z-index overrides).
 */
const BASE_STYLES = `
:host {
    display: block;
    width: 100%;
}

.next-htmx-bs5-ext {
    width: 100%;
}

/* Keep modals above legacy Bootstrap 4 backdrops */
.modal         { z-index: 1060 !important; }
.bs5-backdrop  { z-index: 1059 !important; position: fixed; inset: 0; background: rgba(0,0,0,.5); }
`;

/**
 * Fetch all CSS files, patch them, and prepend them into the shadow root.
 *
 * @param {ShadowRoot} shadowRoot
 * @param {string[]}   cssFiles - URLs to fetch
 */
export async function loadAndPatchCSS(shadowRoot, cssFiles) {
    const results = await Promise.all(
        cssFiles.map(file => fetch(file).then(r => r.text()))
    );

    let combined = results.join('\n');
    combined = combined.replaceAll(':root', ':host');

    const style = document.createElement('style');
    style.textContent = combined;
    shadowRoot.prepend(style);
}

/**
 * Inject the base (static) styles into the shadow root.
 *
 * @param {ShadowRoot} shadowRoot
 */
export function injectBaseStyles(shadowRoot) {
    const style = document.createElement('style');
    style.textContent = BASE_STYLES;
    shadowRoot.prepend(style);
}

/**
 * In-memory cache so repeated navigation to the same page avoids re-fetching.
 */
const cssCache = new Map();

/**
 * Fetch page-specific CSS files and inject/update a dedicated <style> element
 * in the shadow root. Clears the element when paths is empty (page has no
 * page-specific CSS).
 *
 * @param {ShadowRoot} shadowRoot
 * @param {string[]}   paths - absolute URLs to fetch
 */
export async function updatePageCss(shadowRoot, paths) {
    const key = paths.join('|');
    let pageStyle = shadowRoot.getElementById('next-page-css');
    // Skip if the exact same set of CSS files is already active
    if (pageStyle && pageStyle.dataset.activeKey === key) return;
    if (!pageStyle) {
        pageStyle = document.createElement('style');
        pageStyle.id = 'next-page-css';
        shadowRoot.appendChild(pageStyle);
    }
    if (paths.length === 0) {
        pageStyle.dataset.activeKey = '';
        pageStyle.textContent = '';
        return;
    }
    const texts = await Promise.all(paths.map(url => {
        if (cssCache.has(url)) return Promise.resolve(cssCache.get(url));
        return fetch(url).then(r => {
            const ct = r.headers.get('Content-Type') || '';
            if (!r.ok || ct.includes('text/html')) {
                console.error('[next-htmx] CSS fetch failed or returned HTML for:', url, r.status, ct);
                return '';
            }
            return r.text();
        }).then(t => { cssCache.set(url, t); return t; });
    }));
    pageStyle.dataset.activeKey = key;
    pageStyle.textContent = texts.join('\n').replaceAll(':root', ':host');
}